import React from 'react';
import { X } from 'lucide-react';
import type { Pet } from '../../types/sanity';

interface Filters {
  mainCategory?: string[];
  age?: string[];
  gender?: string[];
  size?: string[];
  tags?: string[];
  status?: string[];
}

interface ActiveFiltersProps {
  filters: Filters;
  setFilters: (filters: Filters) => void;
  pets: Pet[];
}

export function ActiveFilters({ filters, setFilters, pets }: ActiveFiltersProps) {
  if (!Object.values(filters).some(value => value?.length > 0)) {
    return null;
  }

  return (
    <div className="bg-accessible-gray-100 dark:bg-accessible-gray-700 high-contrast:bg-white border border-accessible-gray-200 dark:border-accessible-gray-600 rounded-lg shadow-sm p-4 mb-8 text-accessible-gray-900 dark:text-white high-contrast:text-black" aria-label="Aktywne filtry">
      <div className="flex flex-wrap gap-2">
        {filters.mainCategory?.map(category => (
          <button
            key={category}
            onClick={() => setFilters({
              ...filters,
              mainCategory: filters.mainCategory?.filter(c => c !== category)
            })}
            className="px-3 py-1 bg-accessible-gray-100 dark:bg-accessible-gray-700 rounded-full badge-text flex items-center gap-1 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 transition-colors"
            aria-label={`Usuń filtr: ${category === 'dog' ? 'Pies' : 'Kot'}`}
          >
            <span className="tag-text">{category === 'dog' ? 'Pies' : 'Kot'}</span>
            <X className="w-4 h-4" />
          </button>
        ))}
        {filters.age?.map(age => (
          <button
            key={age}
            onClick={() => setFilters({
              ...filters,
              age: filters.age?.filter(a => a !== age)
            })}
            className="px-3 py-1 bg-accessible-gray-100 dark:bg-accessible-gray-700 rounded-full badge-text flex items-center gap-1 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 transition-colors"
            aria-label={`Usuń filtr: ${age === 'young' ? 'Młody' : age === 'adult' ? 'Dorosły' : 'Senior'}`}
          >
            <span className="tag-text">
              {age === 'young' ? 'Młody' :
               age === 'adult' ? 'Dorosły' :
               'Senior'}
            </span>
            <X className="w-4 h-4" />
          </button>
        ))}
        {filters.gender?.map(currentGender => (
          <button
            key={currentGender}
            onClick={() => setFilters({
              ...filters,
              gender: filters.gender?.filter(g => g !== currentGender)
            })}
            className="px-3 py-1 bg-accessible-gray-100 dark:bg-accessible-gray-700 rounded-full badge-text flex items-center gap-1 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 transition-colors"
            aria-label={`Usuń filtr: ${currentGender === 'male' ? 'Samiec' : 'Samica'}`}
          >
            <span className="tag-text">{currentGender === 'male' ? 'Samiec' : 'Samica'}</span>
            <X className="w-4 h-4" />
          </button>
        ))}
        {filters.size?.map(size => (
          <button
            key={size}
            onClick={() => setFilters({
              ...filters,
              size: filters.size?.filter(s => s !== size)
            })}
            className="px-3 py-1 bg-accessible-gray-100 dark:bg-accessible-gray-700 rounded-full badge-text flex items-center gap-1 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 transition-colors"
            aria-label={`Usuń filtr: ${size === 'mini' ? 'Miniaturka' : size === 'small' ? 'Mały' : size === 'medium' ? 'Średni' : 'Duży'}`}
          >
            <span className="tag-text">
              {size === 'mini' ? 'Miniaturka' :
               size === 'small' ? 'Mały' :
               size === 'medium' ? 'Średni' :
               'Duży'}
            </span>
            <X className="w-4 h-4" />
          </button>
        ))}
        {filters.status?.map(status => (
          <button
            key={status}
            onClick={() => setFilters({
              ...filters,
              status: filters.status?.filter(s => s !== status)
            })}
            className="px-3 py-1 bg-accessible-gray-100 dark:bg-accessible-gray-700 rounded-full badge-text flex items-center gap-1 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 transition-colors"
            aria-label={`Usuń filtr: ${status === 'available' ? 'Do adopcji' : status === 'reserved' ? 'Zarezerwowany' : 'Nie do adopcji'}`}
          >
            <span className="tag-text">
              {status === 'available' ? 'Do adopcji' :
               status === 'reserved' ? 'Zarezerwowany' :
               'Nie do adopcji'}
            </span>
            <X className="w-4 h-4" />
          </button>
        ))}
        {filters.tags?.map(tag => {
          const tagObj = pets.find(pet => 
            pet.tags?.some(t => t.slug.current === tag)
          )?.tags?.find(t => t.slug.current === tag);
          
          return (
            <button
              key={tag}
              onClick={() => setFilters({
                ...filters,
                tags: filters.tags?.filter(t => t !== tag)
              })}
              className="px-3 py-1 bg-accessible-gray-100 dark:bg-accessible-gray-700 rounded-full badge-text flex items-center gap-1 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 transition-colors"
              aria-label={`Usuń filtr: ${tagObj?.label || tag}`}
            >
              <span className="tag-text">{tagObj?.label || tag}</span>
              <X className="w-4 h-4" />
            </button>
          );
        })}
      </div>
    </div>
  );
}